import { useCallback, useEffect, useState } from 'react'
import useApi from './useApi'
import { realCoordinates } from '../lib/location'

/**
 * gets community map pins for a profile
 *
 * @param {'user' | 'business'} profileType
 * @param {string | number | null} idOrSlug - User id, or business id/slug
 * @returns {{ pins: Array<{ event_id: number, event_slug: string, lat: number, lng: number, thumbnail_url: string, title: string, pin_type: string }>, loading: boolean, error: Error | null }}
 */
export default function useCommunityMapPins(profileType, idOrSlug) {
  const { getBusinessCommunityMapPins, getUserCommunityMapPins } = useApi()
  const [pins, setPins] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const fetchPins = useCallback(async () => {
    if (profileType === 'business') {
      return getBusinessCommunityMapPins(idOrSlug)
    }
    if (profileType === 'user') {
      return getUserCommunityMapPins(idOrSlug)
    }
    return { data: { pins: [] } }
  }, [
    profileType,
    idOrSlug,
    getBusinessCommunityMapPins,
    getUserCommunityMapPins,
  ])

  useEffect(() => {
    const enabled =
      (profileType === 'user' || profileType === 'business') &&
      idOrSlug != null &&
      idOrSlug !== ''

    if (!enabled) {
      setPins([])
      setLoading(false)
      setError(null)
      return
    }

    let cancelled = false
    setError(null)
    setLoading(true)

    const run = async () => {
      try {
        const { data } = await fetchPins()
        if (!cancelled && data?.pins) {
          setPins(
            Array.isArray(data.pins)
              ? data.pins.filter(pin => realCoordinates(pin.lat, pin.lng))
              : [],
          )
        } else if (!cancelled) {
          setPins([])
        }
      } catch (err) {
        if (!cancelled) {
          setError(err)
          setPins([])
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [profileType, idOrSlug, fetchPins])

  return { pins, loading, error }
}
